import axios from '@/libs/axios'

/**
 * Servicio de autenticación
 */
class AuthService {
  constructor() {
    this.baseURL = '/auth'
  }

  /**
   * Inicia sesión con email y contraseña
   */
  async login(credentials) {
    try {
      const response = await axios.post(`${this.baseURL}/login`, credentials)
      
      // Guardar token
      if (response.data && response.data.token) {
        this.setToken(response.data.token)
        if (response.data.refreshToken) {
          this.setRefreshToken(response.data.refreshToken)
        }
        
        // Cargar información del usuario
        await this.loadCurrentUser()
      }
      
      return response.data
    } catch (error) {
      console.error('Error en login:', error)
      throw error
    }
  }

  /**
   * Renueva el access token usando el refresh token
   */
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) throw new Error('No hay refresh token disponible')
    const response = await axios.post(`${this.baseURL}/refresh`, { refreshToken })
    if (response.data?.token) {
      this.setToken(response.data.token)
    }
    if (response.data?.refreshToken) {
      this.setRefreshToken(response.data.refreshToken)
    }
    return response.data
  }

  /**
   * Carga la información del usuario actual desde el servidor
   */
  async loadCurrentUser() {
    try {
      const response = await axios.get('/usuarios/current')
      if (response.data) {
        this.setCurrentUser(response.data)
        return response.data
      }
      return null
    } catch (error) {
      console.error('Error al cargar usuario:', error)
      return null
    }
  }

  /**
   * Registra un nuevo usuario
   */
  async register(userData) {
    try {
      const response = await axios.post(`${this.baseURL}/register`, userData)
      return response.data
    } catch (error) {
      console.error('Error en registro:', error)
      throw error
    }
  }

  /**
   * Solicita restablecimiento de contraseña
   */
  async resetPassword(resetData) {
    try {
      const response = await axios.post(`${this.baseURL}/reset-password`, resetData)
      return response.data
    } catch (error) {
      console.error('Error al solicitar restablecimiento:', error)
      throw error
    }
  }

  /**
   * Valida un token de restablecimiento
   */
  async validateResetToken(token) {
    try {
      console.log('🔍 Enviando petición de validación de token:', {
        url: `${this.baseURL}/validate-reset-token`,
        token: token,
        data: { email: token }
      })
      
      const response = await axios.post(`${this.baseURL}/validate-reset-token`, { email: token })
      
      console.log('✅ Respuesta de validación recibida:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ Error al validar token:', error)
      console.error('Detalles del error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      })
      throw error
    }
  }

  /**
   * Restablece la contraseña con un token
   */
  async resetPasswordWithToken(resetData) {
    try {
      const response = await axios.post(`${this.baseURL}/reset-password-with-token`, resetData)
      return response.data
    } catch (error) {
      console.error('Error al restablecer contraseña con token:', error)
      throw error
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  logout() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('refreshToken')
    // Redirigir al login
    window.location.href = '/gestioncomercial/login'
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated() {
    const token = localStorage.getItem('token')
    return token && token !== 'null' && token !== 'undefined'
  }

  /**
   * Obtiene el token actual
   */
  getToken() {
    return localStorage.getItem('token')
  }

  /**
   * Establece el token en el almacenamiento local
   */
  setToken(token) {
    localStorage.setItem('token', token)
  }

  /**
   * Obtiene el refresh token actual
   */
  getRefreshToken() {
    return localStorage.getItem('refreshToken')
  }

  /**
   * Establece el refresh token en el almacenamiento local
   */
  setRefreshToken(refreshToken) {
    localStorage.setItem('refreshToken', refreshToken)
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUser() {
    const user = localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  }

  /**
   * Establece el usuario actual
   */
  setCurrentUser(user) {
    localStorage.setItem('user', JSON.stringify(user))
  }

  /**
   * Verifica si el token es válido haciendo una petición al servidor
   */
  async validateToken() {
    try {
      const token = this.getToken()
      if (!token) return false

      // Hacer una petición a un endpoint protegido para verificar el token
      const response = await axios.get('/usuarios/current')
      return response.status === 200
    } catch (error) {
      console.error('Token inválido:', error)
      this.logout()
      return false
    }
  }
}

export default new AuthService()
